/**
 * Plain-English consequence copy for a persona change.
 *
 * The confirmation dialog must state what the change actually does, not just
 * ask "are you sure?". Capabilities below mirror the gateway RBAC table
 * (`services/gateway/src/rbac-table.ts`) and the authorization model; keep them
 * in sync when a persona gains or loses a right.
 */
import { PERSONA_LABELS, type Persona } from '../../lib/personas';

/** Capability keys, ordered so the generated sentence reads naturally. */
const CAPABILITIES = [
  'manage team settings and user roles',
  'enter plays and resolve stat conflicts',
  'see coach-only play-calling analytics',
  'view team statistics and game summaries',
] as const;

type Capability = (typeof CAPABILITIES)[number];

const PERSONA_CAPABILITIES: Record<Persona, ReadonlySet<Capability>> = {
  platform_admin: new Set(CAPABILITIES),
  team_admin: new Set(CAPABILITIES),
  offensive_coordinator: new Set([CAPABILITIES[2], CAPABILITIES[3]]),
  defensive_coordinator: new Set([CAPABILITIES[2], CAPABILITIES[3]]),
  coach: new Set([CAPABILITIES[2], CAPABILITIES[3]]),
  primary_stat_operator: new Set([CAPABILITIES[1], CAPABILITIES[3]]),
  general_stat_operator: new Set([CAPABILITIES[1], CAPABILITIES[3]]),
  fanatic: new Set([CAPABILITIES[3]]),
};

/** MFA-required personas (security/authz-model), surfaced as an extra warning. */
const MFA_REQUIRED: ReadonlySet<Persona> = new Set<Persona>([
  'platform_admin',
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
]);

export interface PersonaChangeConsequence {
  /** One-line summary shown as the dialog's headline. */
  summary: string;
  gained: string[];
  lost: string[];
  /** Extra caution lines, e.g. "will be prompted to enrol in MFA". */
  notes: string[];
}

export function describePersonaChange(
  email: string,
  from: Persona | null,
  to: Persona
): PersonaChangeConsequence {
  const before = from === null ? new Set<Capability>() : PERSONA_CAPABILITIES[from];
  const after = PERSONA_CAPABILITIES[to];
  const gained = CAPABILITIES.filter((cap) => after.has(cap) && !before.has(cap));
  const lost = CAPABILITIES.filter((cap) => before.has(cap) && !after.has(cap));

  const notes: string[] = [];
  if (!MFA_REQUIRED.has(from ?? 'fanatic') && MFA_REQUIRED.has(to)) {
    notes.push(
      'They will be required to enrol in two-factor authentication at their next sign-in.'
    );
  }
  if (to === 'fanatic') {
    notes.push('Fanatic accounts do not consume a paid staff seat.');
  }

  const fromLabel = from === null ? 'no persona' : PERSONA_LABELS[from];
  return {
    summary: `Changing ${email} from ${fromLabel} to ${PERSONA_LABELS[to]} updates their dashboard and permissions immediately.`,
    gained,
    lost,
    notes,
  };
}
